<style lang="scss">
.playing-banner {
  flex-direction: column;
  background-color: $purple-dark;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  gap: 16px;

  .logo-wrapper {
    .favourite-icon {
      position: absolute;
      top: -1px;
      right: 5px;
      font-size: 20pt;
      color: white;
      cursor: pointer;

      i {
        text-shadow: 1px 1px 5px black;
      }

      &.active {
        color: $purple;
      }
    }
  }

  &__logo {
    width: 192px;
    height: 192px;
    border-radius: 20px;
    box-shadow: 5px 5px 15px #888, -1px -2px 10px #fff;
    object-fit: cover;
  }

  .station-info {
    text-align: center;
    width: 100%;

    .station-title {
      line-height: 1.3;
    }

    .loading-spinner-wrapper {
      display: inline-flex;
      align-items: center;
      .v-progress-circular {
        width: 1.25rem !important;
        height: 1.25rem !important;
      }
    }
  }


  .player-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 350px;
    min-width: 300px;

    .pause-play-btn {
      background-color: black;
      border-radius: 50%;
      font-size: 1.8rem;
      width: 2.8rem;
      height: 2.8rem;
      min-width: 2.8rem;
      min-height: 2.8rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: opacity 0.2s ease;
      margin-right: 1rem;

      &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .speaker-icon {
      font-size: 1.4rem;
      cursor: pointer;
      margin-right: 0.5rem;

      &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .volume-slider {
      flex: 1;
      max-width: 200px;
    }

    .v-slider {
      &.v-slider--disabled {
        opacity: 0.6;
      }
    }
    .v-slider__thumb {
      width: 20px;
      height: 20px;
    }
    .v-input {
      max-height: 33px;
    }
    .v-slider__track-container {
      height: 10px;
    }
  }
}
</style>

<template>
  <div class="playing-banner-wrapper">
    <section v-if="stationDetails"
             :style="{backgroundImage: 'url(' + getBackgroundUrl() + ')'}"
             class="playing-banner pa-8 d-flex flex-column justify-center align-center">

      <!-- Row 1: The station image (centered by parent) -->
      <div class="logo-wrapper p-relative">
        <img :src="getImgUrl(stationDetails.image_url)" alt="station logo" class="playing-banner__logo">
        <span
          v-if="!isParcVersion"
          :class="{'active': isFavourite}"
          class="favourite-icon"
          @click="toggleFavourite"
        >
          <i class="fas fa-heart"></i>
        </span>
      </div>

      <!-- Row 2: Station Name and Visualizer (centered by parent) -->
      <div class="station-info">
        <p class="white--text font-weight-bold text-h6 station-title mt-2 mb-0">
          <span>{{ stationPrefix }} {{ stationDetails.name }}</span>
          <span v-if="isLoading" class="loading-spinner-wrapper ml-2">
            <v-progress-circular indeterminate width="4" color="white" />
          </span>
        </p>
      </div>

      <!-- Row 3: The Player Controls (centered by parent, with a fixed width) -->
      <div class="player-controls mt-2">
        <!-- Play/Pause Button -->
        <span @click="emitPause(true)" v-if="isPlaying" class="pause-btn pause-play-btn d-flex">
          <i class="fas fa-pause-circle white--text"></i>
        </span>
        <span @click="emitPause(false)" v-else class="play-btn pause-play-btn d-flex">
          <i class="fas fa-play-circle white--text"></i>
        </span>

        <!-- Mute/Unmute Button -->
        <span @click="emitMute(true)" v-if="!muted" class="speaker-icon white--text">
          <i class="fas fa-volume-up"></i>
        </span>
        <span @click="emitMute(false)" v-else class="speaker-icon white--text">
          <i class="fas fa-volume-mute"></i>
        </span>

        <!-- Volume Slider -->
        <v-slider
          @change="emitVolume"
          :disabled="isLoading"
          color="white"
          thumb-color="#9938B9"
          v-model="localVolume"
          step="0.05"
          max="1"
          hide-details
          class="volume-slider"
          aria-label="Volume"
        />
      </div>

    </section>

    <section v-else class="pa-8 text-center">
      <p>Select a station to start listening.</p>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import radioService from '../services/radioService';
const lightImg = require(`../assets/radio-light.jpg`);
const darkImg = require(`../assets/radio-dark.jpg`);

export default {
  name: 'PlayingBanner',
  computed: {
    ...mapState('radio', {
      stationDetails: state => state.currentStation.details,
      muted: state => state.currentStation.muted,
      volumeState: state => state.currentStation.volume,
      isPlaying: state => state.isPlaying,
      isLoading: state => state.isLoading,
    }),
    ...mapState({
      favourites: state => state.favourites.favourites,
      darkMode: state => state.darkMode,
    }),
    isFavourite(){
      if (!this.stationDetails) {
        return false;
      }
      let favourite = false;
      this.favourites.forEach((item) => {
        if(item.name === this.stationDetails.name){
          favourite = true;
        }
      });
      return favourite;
    },
    stationPrefix() {
      if (this.isLoading) {
        return 'Loading';
      }
      if (this.isPlaying) {
        return 'Now playing';
      }
      return '';
    }
  },
  data() {
    return {
      localVolume: 1,
    };
  },
  watch: {
    volumeState(newVol) {
      if (this.localVolume !== newVol) {
        this.localVolume = newVol;
      }
    },
    stationDetails: {
      handler(newDetails) {
        if (newDetails) {
          this.localVolume = this.volumeState;
        }
      },
      immediate: true
    }
  },
  methods: {
    getBackgroundUrl() {
      return !this.darkMode ? lightImg : darkImg;
    },
    getImgUrl(url) {
      return radioService.getImgUrl(url);
    },
    emitPause(value) {
      this.$emit('pause', value);
    },
    emitMute(value) {
      this.$emit('mute', value);
    },
    emitVolume() {
      this.$emit('update-volume', this.localVolume);
    },
    async toggleFavourite() {
      if (this.isLoading || !this.stationDetails) {
        return;
      }
      try {
        const isNowFavourite = await this.$store.dispatch('favourites/toggleFavourite', this.stationDetails);
        if (isNowFavourite) {
          this.$toasted.success(`${this.stationDetails.name} added to favourites!`);
        } else {
          this.$toasted.show(`${this.stationDetails.name} removed from favourites.`);
        }
      } catch (err) {
        console.error("PlayingBanner: Error toggling favourite:", err);
        this.$toasted.error("Could not update favourites.");
      }
    }
  },
};
</script>